import * as CookieConsent from "vanilla-cookieconsent";

import { getCookieByName } from "@utils/helper";

export const CONSENT_CHANGE_EVENT = "cookieconsent:change";
export const CONSENT_READY_EVENT = "cookieconsent:ready";

const PRIVACY_POLICY_URL = "/privacy-policy/";
const COOKIE_LIST_URL = "/privacy-policy/#cookies";

export function getVisitorRegion() {
  if (typeof document === "undefined") {
    return process.env.GATSBY_DEFAULT_VISITOR_REGION || "default";
  }

  return getCookieByName("visitor_region") ||
    process.env.GATSBY_DEFAULT_VISITOR_REGION ||
    "default";
}

export function isOptInRegion(region = getVisitorRegion()) {
  return region === "eu";
}

function getStoredConsentCategories() {
  const cc = getCookieByName("cc_cookie");

  if (!cc) {
    return null;
  }

  try {
    const data = JSON.parse(decodeURIComponent(cc));
    return data.categories || [];
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent() {
  if (typeof window === "undefined") {
    return false;
  }

  const storedCategories = getStoredConsentCategories();

  // Saved preference in cc_cookie always wins.
  if (storedCategories !== null) {
    return storedCategories.includes("analytics");
  }

  // No saved choice yet — match region default (opt-out = on, opt-in = off).
  // Do not use CookieConsent.acceptedCategory() here: in opt-out mode it returns
  // false before the user dismisses the banner, which incorrectly denies gtag.
  return !isOptInRegion();
}

// Advertising is opt-in everywhere: denied until the visitor explicitly allows it.
// This also honours Global Privacy Control, since no ads signal is ever granted by default.
export function hasAdsConsent() {
  if (typeof window === "undefined") {
    return false;
  }

  const storedCategories = getStoredConsentCategories();
  return storedCategories !== null && storedCategories.includes("ads");
}

function clearAnalyticsCookies() {
  if (typeof document === "undefined") {
    return;
  }

  const hostname = window.location.hostname;
  const domains = new Set([hostname, `.${hostname}`]);
  const parts = hostname.split(".");

  if (parts.length >= 2) {
    domains.add(`.${parts.slice(-2).join(".")}`);
  }

  if (parts.length >= 3) {
    domains.add(`.${parts.slice(-3).join(".")}`);
  }

  const cookieNames = document.cookie
    .split(";")
    .map((part) => part.trim().split("=")[0])
    .filter((name) => /^(?:_ga|_gid|_gat)/.test(name));

  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const expired = "Thu, 01 Jan 1970 00:00:00 GMT";

  cookieNames.forEach((name) => {
    domains.forEach((domain) => {
      document.cookie = `${name}=; Expires=${expired}; Max-Age=0; Path=/; Domain=${domain}${secure}`;
    });
    document.cookie = `${name}=; Expires=${expired}; Max-Age=0; Path=/${secure}`;
  });
}

let pendingClearTimeouts = [];

function cancelPendingAnalyticsCookieClears() {
  pendingClearTimeouts.forEach(clearTimeout);
  pendingClearTimeouts = [];
}

function scheduleAnalyticsCookieClears() {
  cancelPendingAnalyticsCookieClears();
  clearAnalyticsCookies();

  [150, 1000].forEach((delay) => {
    const timeoutId = window.setTimeout(() => {
      if (!hasAnalyticsConsent()) {
        clearAnalyticsCookies();
      }
    }, delay);
    pendingClearTimeouts.push(timeoutId);
  });
}

export function updateGtagConsent() {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  const granted = hasAnalyticsConsent() ? "granted" : "denied";
  const adsGranted = hasAdsConsent() ? "granted" : "denied";

  window.gtag("consent", "update", {
    analytics_storage: granted,
    ad_storage: adsGranted,
    ad_user_data: adsGranted,
    ad_personalization: adsGranted,
    security_storage: "granted",
  });
  window.gtag("set", "ads_data_redaction", adsGranted === "denied");

  if (granted === "denied") {
    scheduleAnalyticsCookieClears();
  } else {
    cancelPendingAnalyticsCookieClears();
  }

  window.dispatchEvent(
    new CustomEvent(CONSENT_CHANGE_EVENT, {
      detail: { granted: granted === "granted" },
    }),
  );
}

export function showCookieSettings() {
  CookieConsent.showPreferences();
}

function getPreferencesSections() {
  return [
    {
      title: "Cookie usage",
      description: `We use cookies to keep the website working, to analyze traffic and, only if you allow it, to show and measure ads. You can choose which optional cookies to allow. See the <a href="${COOKIE_LIST_URL}" class="cc-link">full list of cookies</a> in our Privacy Policy.`,
    },
    {
      title: "Strictly necessary cookies",
      description:
        "These cookies are required to store your cookie preferences and keep the site working.",
      linkedCategory: "necessary",
    },
    {
      title: "Analytics cookies",
      description:
        "These cookies help us understand how visitors use the site through Google Analytics 4.",
      linkedCategory: "analytics",
      cookieTable: {
        headers: {
          name: "Cookie",
          domain: "Domain",
          description: "Description",
          expiration: "Expiration",
        },
        body: [
          {
            name: "_ga",
            domain: "Google Analytics",
            description: "Distinguishes users for analytics reporting.",
            expiration: "2 years",
          },
          {
            name: "_ga_*",
            domain: "Google Analytics",
            description: "Stores session state for GA4.",
            expiration: "2 years",
          },
        ],
      },
    },
    {
      title: "Advertising cookies",
      description:
        "These cookies let Google AdSense show ads, limit how often you see them, measure their performance and, if you allow personalization, tailor them to your interests. They are off unless you turn them on.",
      linkedCategory: "ads",
      cookieTable: {
        headers: {
          name: "Cookie",
          domain: "Domain",
          description: "Description",
          expiration: "Expiration",
        },
        body: [
          {
            name: "__gads, __gpi",
            domain: "Google AdSense",
            description: "Serves and measures ads and limits repeat ads.",
            expiration: "13 months",
          },
          {
            name: "__eoi",
            domain: "Google AdSense",
            description: "Helps prevent ad fraud and measure ads.",
            expiration: "6 months",
          },
        ],
      },
    },
    {
      title: "More information",
      description: `Questions about cookies or your data? Read our <a href="${PRIVACY_POLICY_URL}" class="cc-link">Privacy Policy</a> or contact <a href="mailto:info@maisonchristie.com" class="cc-link">info@maisonchristie.com</a>.`,
    },
  ];
}

function getTranslations(optIn) {
  const privacyLink = `<a href="${PRIVACY_POLICY_URL}" class="cc-link">Privacy Policy</a>`;

  if (optIn) {
    return {
      consentModal: {
        title: "We use cookies",
        // Kept short: on phones a long notice covers the tool visitors came for
        description: `We use optional cookies for analytics and ads, only if you accept. Change your choice anytime in Cookie Settings at the bottom of the page. See our ${privacyLink}.`,
        acceptAllBtn: "Accept all",
        acceptNecessaryBtn: "Reject all",
        showPreferencesBtn: "Manage settings",
      },
      preferencesModal: {
        title: "Cookie Settings",
        savePreferencesBtn: "Save settings",
        acceptAllBtn: "Accept all",
        acceptNecessaryBtn: "Reject all",
        closeIconLabel: "Close",
        sections: getPreferencesSections(),
      },
    };
  }

  return {
    consentModal: {
      title: "We use cookies",
      // Kept short: on phones a long notice covers the tool visitors came for
      description: `We use analytics cookies to improve the site. Ad cookies are only used if you accept. Change your choice anytime in Cookie Settings at the bottom of the page. See our ${privacyLink}.`,
      acceptAllBtn: "Accept all",
      acceptNecessaryBtn: "Reject all",
      showPreferencesBtn: "Manage settings",
    },
    preferencesModal: {
      title: "Cookie Settings",
      savePreferencesBtn: "Save settings",
      acceptAllBtn: "Accept all",
      acceptNecessaryBtn: "Reject all",
      closeIconLabel: "Close",
      sections: getPreferencesSections(),
    },
  };
}

export async function initCookieConsent() {
  if (typeof window === "undefined") {
    return;
  }

  const region = getVisitorRegion();
  const optIn = isOptInRegion(region);

  await CookieConsent.run({
    mode: optIn ? "opt-in" : "opt-out",
    // Bumped to 3 for the new "ads" category, so everyone is asked again
    revision: 3,
    categories: {
      necessary: {
        readOnly: true,
      },
      analytics: {
        enabled: !optIn,
        autoClear: {
          cookies: [
            { name: /^_ga/ },
            { name: "_gid" },
            { name: /^_gat/ },
          ],
        },
      },
      ads: {
        enabled: false,
        autoClear: {
          cookies: [
            { name: "__gads" },
            { name: "__gpi" },
            { name: "__eoi" },
          ],
        },
      },
    },
    language: {
      default: "en",
      translations: {
        en: getTranslations(optIn),
      },
    },
    guiOptions: {
      consentModal: {
        layout: "box",
        position: "bottom right",
        equalWeightButtons: true,
      },
      preferencesModal: {
        layout: "box",
        position: "right",
        equalWeightButtons: true,
      },
    },
    onConsent: () => {
      updateGtagConsent();
    },
    onChange: () => {
      updateGtagConsent();
    },
  });

  updateGtagConsent();
  window.dispatchEvent(new Event(CONSENT_READY_EVENT));
}

export function onAnalyticsConsentReady(callback) {
  if (typeof window === "undefined") {
    return () => {};
  }

  const runIfAllowed = () => {
    if (hasAnalyticsConsent()) {
      callback();
    }
  };

  runIfAllowed();

  const handleConsentEvent = (event) => {
    if (event.detail?.granted) {
      callback();
    }
  };

  window.addEventListener(CONSENT_READY_EVENT, runIfAllowed);
  window.addEventListener(CONSENT_CHANGE_EVENT, handleConsentEvent);

  return () => {
    window.removeEventListener(CONSENT_READY_EVENT, runIfAllowed);
    window.removeEventListener(CONSENT_CHANGE_EVENT, handleConsentEvent);
  };
}
